import { ExternalLink, RefreshCw } from 'lucide-react';
import { useEffect, useState } from 'react';
import { api, getApiErrorMessage } from '../../api/client';
import type { CargoInvoiceDetail, CargoLedgerEntry } from '../../api/types';
import { config } from '../../config';
import {
  formatDateTime,
  formatUsdt,
  canRetryCargoInvoiceSplit,
  invoicePaymentUrl,
  invoiceStatusBadge,
  invoiceStatusLabel,
  splitStatusBadge,
  splitStatusLabel,
  tronscanTxUrl,
} from '../../utils/cargoStatus';
import { Modal } from '../ui/Modal';

type Props = {
  hashId: string | null;
  open: boolean;
  onClose: () => void;
  canManage: boolean;
  onUpdated?: () => void;
};

export function CargoInvoiceDetailModal({ hashId, open, onClose, canManage, onUpdated }: Props) {
  const [invoice, setInvoice] = useState<CargoInvoiceDetail | null>(null);
  const [ledger, setLedger] = useState<CargoLedgerEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !hashId) return;
    setLoading(true);
    setError(null);
    void api
      .getCargoInvoice(hashId)
      .then((res) => {
        setInvoice(res.invoice);
        setLedger(res.ledger);
      })
      .catch((e) => setError(getApiErrorMessage(e)))
      .finally(() => setLoading(false));
  }, [open, hashId]);

  const retrySplit = async () => {
    if (!hashId) return;
    setActionLoading(true);
    try {
      await api.retryCargoInvoiceSplit(hashId);
      const res = await api.getCargoInvoice(hashId);
      setInvoice(res.invoice);
      setLedger(res.ledger);
      onUpdated?.();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  const mockVerify = async () => {
    if (!hashId) return;
    setActionLoading(true);
    try {
      await api.mockVerifyCargoInvoice(hashId);
      const res = await api.getCargoInvoice(hashId);
      setInvoice(res.invoice);
      setLedger(res.ledger);
      onUpdated?.();
    } catch (e) {
      setError(getApiErrorMessage(e));
    } finally {
      setActionLoading(false);
    }
  };

  const payUrl = hashId ? invoicePaymentUrl(hashId, config.invoicePayBaseUrl) : '';
  const showRetrySplit = Boolean(canManage && invoice && canRetryCargoInvoiceSplit(invoice));

  return (
    <Modal
      title={hashId ? `Invoice ${hashId}` : 'Invoice'}
      open={open}
      onClose={onClose}
      footer={
        canManage && invoice ? (
          <>
            {showRetrySplit ? (
              <button
                type="button"
                className={invoice.split_status === 'failed' ? 'btn-primary btn-inline' : 'btn-outline btn-inline'}
                disabled={actionLoading}
                onClick={() => void retrySplit()}
              >
                <RefreshCw size={14} aria-hidden className={actionLoading ? 'spin-icon' : undefined} />
                {actionLoading ? 'Retrying split…' : 'Retry split'}
              </button>
            ) : null}
            {invoice.status === 'pending' ? (
              <button
                type="button"
                className="btn-outline btn-inline"
                disabled={actionLoading}
                onClick={() => void mockVerify()}
              >
                Mock verify (dev)
              </button>
            ) : null}
            <button type="button" className="btn-primary btn-inline" onClick={onClose}>
              Close
            </button>
          </>
        ) : (
          <button type="button" className="btn-primary btn-inline" onClick={onClose}>
            Close
          </button>
        )
      }
    >
      {loading ? <p className="cargo-empty-row">Loading…</p> : null}
      {error ? <p className="form-error">{error}</p> : null}
      {showRetrySplit ? (
        <div className="cargo-split-retry-banner">
          <strong>Split not completed.</strong>
          {invoice?.split_error ? ` ${invoice.split_error}` : ' Use Retry split to run commission + vendor payout again.'}
        </div>
      ) : null}
      {invoice ? (
        <div className="cargo-detail-list">
          <div className="cargo-detail-row">
            <span>Partner</span>
            <span>{invoice.company_name || '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Order ID</span>
            <span>{invoice.cargo_id || '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Amount</span>
            <span>{formatUsdt(invoice.amount)} USDT</span>
          </div>
          <div className="cargo-detail-row">
            <span>Status</span>
            <span className={invoiceStatusBadge(invoice.status)}>{invoiceStatusLabel(invoice.status)}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Split</span>
            <span className={splitStatusBadge(invoice.split_status)}>
              {splitStatusLabel(invoice.split_status)}
            </span>
          </div>
          {invoice.split_error ? (
            <div className="cargo-split-error">{invoice.split_error}</div>
          ) : null}
          <div className="cargo-detail-row">
            <span>Deposit address</span>
            <code className="cargo-hash">{invoice.crypto_address || '—'}</code>
          </div>
          <div className="cargo-detail-row">
            <span>Payment link</span>
            <a href={payUrl} target="_blank" rel="noopener noreferrer" className="cargo-tx-link">
              Open <ExternalLink size={12} />
            </a>
          </div>
          <div className="cargo-detail-row">
            <span>Deposit tx</span>
            {tronscanTxUrl(invoice.tx_hash) ? (
              <a href={tronscanTxUrl(invoice.tx_hash)!} target="_blank" rel="noopener noreferrer" className="cargo-tx-link">
                Tronscan <ExternalLink size={12} />
              </a>
            ) : (
              '—'
            )}
          </div>
          <div className="cargo-detail-row">
            <span>Created by</span>
            <span>{invoice.created_by_name || invoice.created_by_email || '—'}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Created</span>
            <span>{formatDateTime(invoice.created_at)}</span>
          </div>
          <div className="cargo-detail-row">
            <span>Expires</span>
            <span>{formatDateTime(invoice.expires_at)}</span>
          </div>
          {invoice.deposit_amount_usdt ? (
            <div className="cargo-detail-row">
              <span>Deposit received</span>
              <span>{invoice.deposit_amount_usdt} USDT</span>
            </div>
          ) : null}
          {invoice.commission_amount_usdt ? (
            <div className="cargo-detail-row">
              <span>Commission (1.2%)</span>
              <span>{invoice.commission_amount_usdt} USDT</span>
            </div>
          ) : null}
          {invoice.vendor_amount_usdt ? (
            <div className="cargo-detail-row">
              <span>Vendor payout (98.8%)</span>
              <span>{invoice.vendor_amount_usdt} USDT</span>
            </div>
          ) : null}
          {ledger.length > 0 ? (
            <div className="cargo-ledger-block">
              <h3 className="cargo-panel-title">Settlement ledger</h3>
              {ledger.map((e) => (
                <div key={e.id} className="cargo-ledger-row">
                  <div>
                    <strong>{e.entry_type}</strong> · {e.amount_display} USDT · {e.status}
                  </div>
                  {e.tx_hash && tronscanTxUrl(e.tx_hash) ? (
                    <a href={tronscanTxUrl(e.tx_hash)!} target="_blank" rel="noopener noreferrer" className="cargo-tx-link">
                      tx <ExternalLink size={11} />
                    </a>
                  ) : null}
                  {e.error_message ? <div className="cargo-split-error">{e.error_message}</div> : null}
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
    </Modal>
  );
}
